import { Landmark, Tags, type LucideIcon } from 'lucide-react';

/** One row of either Cash Division vocabulary. */
export interface CashCatalogEntry {
  id: string;
  name: string;
  description?: string | null;

  /** How many live requests are filed under this row. */
  requestCount: number;

  isActive: boolean;
  createdAt: string;
  createdByName?: string | null;
  updatedAt?: string | null;
  updatedByName?: string | null;
}

export interface CashCatalogRequest {
  name: string;
  description?: string | null;
}

export interface CashCatalogOption {
  id: string;
  name: string;
  description?: string | null;
}

/** The route segment each vocabulary answers on — `/api/cash-catalogs/{slug}`. */
export type CashCatalogSlug = 'transaction-types' | 'payment-types';

/**
 * Everything that differs between the two vocabularies, in one place.
 *
 * **The ROUTES, the Settings rows and the pages all read this**, so a third vocabulary cannot
 * appear in the menu with no route behind it — the shape `PROCUREMENT_CATALOGS` takes.
 */
export interface CashCatalogDescriptor {
  slug: CashCatalogSlug;
  label: string;
  noun: string;
  nounPlural: string;
  description: string;
  emptyMessage: string;
  columnHeader: string;
  fieldDescription: string;
  icon: LucideIcon;
  settingsPath: string;
  /** The CLR name History Logs filters on — two tables, two names. */
  entityType: string;
}

export const CASH_CATALOGS: readonly CashCatalogDescriptor[] = [
  {
    slug: 'transaction-types',
    label: 'Transaction Types',
    noun: 'transaction type',
    nounPlural: 'transaction types',
    description:
      'What a request is for — benefits, operational expenses, reimbursements, bills, cheque issuance, fidelity bonds.',
    emptyMessage:
      'No transaction types yet. Add what the Cash Division disburses for — benefits, reimbursements, bills.',
    columnHeader: 'Transaction type',
    fieldDescription: 'What a disbursement request is for, as the Cash Division files it.',
    icon: Tags,
    settingsPath: '/cash-division/settings/transaction-types',
    entityType: 'CashTransactionType',
  },
  {
    slug: 'payment-types',
    label: 'Payment Types',
    noun: 'payment type',
    nounPlural: 'payment types',
    description: 'How a request is paid out — EMDS (Landbank), LD.ADA, cheques.',
    emptyMessage: 'No payment types yet. Add how the Cash Division pays out — EMDS (Landbank), LD.ADA, cheques.',
    columnHeader: 'Payment type',
    fieldDescription: 'How the disbursement is paid out to the requesting party.',
    icon: Landmark,
    settingsPath: '/cash-division/settings/payment-types',
    entityType: 'CashPaymentType',
  },
];

export function cashCatalog(slug: string): CashCatalogDescriptor | undefined {
  return CASH_CATALOGS.find((c) => c.slug === slug);
}
